import React, { useMemo } from 'react';
import { Breadcrumbs, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CustomBreadcrumbs.css';

/**
 * CustomBreadcrumbs
 *
 * Handles:
 *   • Regular breadcrumb trails (array of i18n keys in state.breadcrumbs)
 *   • Permanent-file suffix  (" - Permanent File") or custom title suffix (" - <titleName>")
 *   • Sort-code drill-down   → "Account Card Report: <accountCardName>"
 *
 * Props
 * -----
 * direction        "ltr" | "rtl"  – direction class supplied by MainLayout
 * isPermenantCase  boolean        – optional prop (used when reloading OtherDocument)
 */
const CustomBreadcrumbs = ({
  direction,
  isPermenantCase: isPermFromProp,
}) => {
  const { t, i18n }              = useTranslation();
  const { state = {}, pathname } = useLocation();

  // Values arriving from navigate(..., { state })
  const {
    breadcrumbs: rawBreadcrumbs,
    breadcrumbLabels,
    type,
    accountCardName,
    accountCardId,
    isSortCodeReport,
    isPermenantCase: isPermFromState,
    titleId,
    titleName,
    isEdit,
  } = state || {};

  // Permanent-file flag: prop overrides state
  const isActualPermanent = isPermFromProp ?? isPermFromState ?? false;

  /* ------------------------------------------------------------------ */
  /* Route-derived label                                                */
  /* ------------------------------------------------------------------ */
  /* Router state does not survive a browser refresh, and some pages
     replace it to store fresh data. Without it the trail used to fall
     back to "add client", so name the page after its own route instead. */
  const routeCrumb = useMemo(() => {
    const segments = (pathname || '').split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      const segment = segments[i];
      if (i18n?.exists?.(segment)) return t(segment);
    }
    return null;
  }, [pathname, i18n, t]);

  /* ------------------------------------------------------------------ */
  /* Translate the base breadcrumb array                                */
  /* ------------------------------------------------------------------ */
  const baseCrumbs =
    Array.isArray(breadcrumbLabels) && breadcrumbLabels.length
      ? breadcrumbLabels
      : Array.isArray(rawBreadcrumbs) && rawBreadcrumbs.length
        ? rawBreadcrumbs.map((key) => t(key))
        : type
          ? [t('Archive'), t(type)]
          : isEdit
            ? [t('EditClient')]
            : routeCrumb
              ? [routeCrumb]
              : [t('addClientTitle')];


  const isSingleClientLabel =
    baseCrumbs.length === 1 &&
    (isEdit ||
      baseCrumbs[0] === t('addClientTitle') ||
      baseCrumbs[0] === t('OfficeAccountManagers') ||
      baseCrumbs[0] === t('UserActivities') ||
      baseCrumbs[0] === t('TrackingList') ||
      baseCrumbs[0] === t('CreditTerminal') ||
      baseCrumbs[0] === t('Finsite'));


  const breadcrumbs = baseCrumbs.map((label, idx) => {
    const isLast = idx === baseCrumbs.length - 1;

    // A. Custom title or permanent file ⇒ add suffix
    if (isLast && titleId && titleName) {
      return `${label} - ${titleName}`;
    }
    if (isLast && isActualPermanent) {
      return `${label} - ${t('PermanentFile')}`;
    }

    // B. Sort-code drill-down ⇒ replace last crumb
    if (isLast && isSortCodeReport && accountCardName && accountCardId) {
      
      return `${t('LedgerReport')}:   ${accountCardId} - ${accountCardName}`;
    }

    return label;
  }); 

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div className={`xtram-breadcrumbs ${direction || ''}`} dir={direction}>
      <Breadcrumbs
        className="xtram-breadcrumbs__trail"
        separator={
          <span className="xtram-breadcrumbs__sep" aria-hidden="true">
            <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
              <path
                d="M4 2.2 8.2 6 4 9.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        }
        dir={direction}
      >
        {breadcrumbs.map((label, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <Typography
              key={index}
              component="span"
              variant="inherit"
              className={
                isLast || isSingleClientLabel
                  ? 'xtram-breadcrumbs__current'
                  : 'xtram-breadcrumbs__parent'
              }
            >
              {label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </div>
  );
};

export default CustomBreadcrumbs;
